import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  Grid,
  Divider,
  Paper,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import PaymentIcon from "@mui/icons-material/Payment";
import DynamicInput from "./DynamicInput";
import { Email } from "@mui/icons-material";
import { formatPrice } from "utils/index";
import { useAppSelector } from "store";
import { useEffect, useState } from "react";
import { getTripDetails } from "services/createTripService";
import { BookingDetailsType } from "../utils";

// Define proper TypeScript interfaces
interface PriceTier {
  name: string;
  numberOfSeats: number;
  price: string;
}

interface TravellerDetail {
  name: string;
  gender: string;
  age: number;
  id?: string;
  number?: string;
  email?: string;
}
interface BookingDetailsProps {
  data: BookingDetailsType;
  isReadOnly?: boolean;
  showDiscount?: boolean;
}

const BookingDetails: React.FC<BookingDetailsProps> = ({
  data,
  isReadOnly = true,
  showDiscount = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const organization = useAppSelector((state) => state?.merchantForm?.organisationDetails) as any;
  const [duration,setDuration] = useState({
    days:'0',nights:'0'
  })

  useEffect(()=>{
    const fetchTrips = async () => { 
      const response = await getTripDetails(data?.tripId);
      if (response) {
        setDuration({
          days: response?.days || '0',
          nights: response?.nights || '0',
        });
      }
    }

    fetchTrips();
    
  },[data?.tripId])

  // Get appropriate status color based on data? status
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
        return "success";
      case "pending":
        return "warning";
      case "cancelled":
        return "error";
      default:
        return "primary";
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    });
  };

  // Check if journey date is within 7 days
  const shouldShowContactInfo = () => {
    if (!data?.slotStartDate) return false;
    
    const currentDate = new Date();
    const slotDate = new Date(data.slotStartDate);
    
    // Calculate difference in days
    const diffTime = slotDate.getTime() - currentDate.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    const thresholdDays = organization?.brandName === "Tripper Trails" ? 30 : 15;
    
    // Return true if difference is 15 days or less
    return Math.floor(diffDays) <= thresholdDays;
  };

  if (!data) {
    return (
      <Card sx={{ marginTop: 3, padding: 2, width: "100%" }}>
        <CardContent>
          <Typography variant="h6" color="error">
            Booking data not available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const bookingId = `${data?.searchIdKey}${data?.searchId}`;

  const isDiscountApplied = showDiscount &&  data?.priceBreakUp?.couponDiscount && Number(data?.priceBreakUp?.couponDiscount) !== 0;
  const showDiscountedPrice = showDiscount && isDiscountApplied && data?.priceBreakUp?.discountedPrice && Number(data?.priceBreakUp?.discountedPrice) !== 0;

  return (
    <Card
      sx={{
        marginTop: 3,
        padding: { xs: 1, sm: 2 },
        width: "100%",
      }}
    >
      <CardContent sx={{ padding: { xs: 1, sm: 2 } }}>
        <Typography
          fontWeight={600}
          variant={isMobile ? "h6" : "h5"}
          color="primary"
          marginBottom={2}
          display="flex"
          alignItems="center"
        >
          <PaymentIcon sx={{ mr: 1 }} /> Booking Information
        </Typography>

        <Accordion
          expanded={true}
          sx={{
            marginY: 2,
            "&:before": { display: "none" },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "12px !important",
            boxShadow: 2,
            transition: "all 0.3s ease-in-out",
            overflow: "hidden",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.03)",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.05)",
              },
              padding: { xs: "8px 16px", md: "16px 24px" },
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                flexWrap: { xs: "wrap", md: "nowrap" },
                gap: 1,
                mb: isMobile ? 1 : 0,
              }}
            >
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                color="text.primary"
                fontWeight={500}
                sx={{
                  flexShrink: 0,
                  marginRight: { xs: 0, sm: 2 },
                  mb: isMobile ? 0.5 : 0,
                  width: isMobile ? "100%" : "auto",
                }}
              >
                Booking: {bookingId}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mr: { xs: 2, md: 3 },
                  mb: isMobile ? 0.5 : 0,
                }}
              >
                <PersonIcon fontSize="small" sx={{ mr: 0.5 }} />
                {data?.numberOfTravellers} Travellers
              </Typography>

              <Typography
                variant="body1"
                color="text.primary"
                fontWeight={600}
                sx={{
                  flexShrink: 0,
                  mr: { xs: 2, md: 3 },
                  mb: isMobile ? 0.5 : 0,
                }}
              >
                {formatPrice(data?.totalTripCost,true)}
              </Typography>

              <Chip
                label={`${data?.bookingStatus}`}
                color={getStatusColor(data?.bookingStatus) as any}
                variant="filled"
                size="small"
                sx={{
                  marginLeft: { xs: 0, md: "auto" },
                  fontWeight: 500,
                  height: 24,
                }}
              />
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ padding: { xs: 2, sm: 3 } }}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                  }}
                >
                  <DynamicInput
                    name="tripTitle"
                    label="Trip Title"
                    type="text"
                    placeholder="Trip Title"
                    required
                    disabled={true}
                    value={data?.tripTitle}
                  />

                  <DynamicInput
                    name="slotStartDate"
                    label="Start Date"
                    type="text"
                    placeholder="Start Date"
                    required
                    disabled={true}
                    value={formatDate(data?.slotStartDate)}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 1, sm: 2 },
                  }}
                >
                  <DynamicInput
                    name="bookingId"
                    label="Booking ID"
                    type="text"
                    placeholder="Booking ID"
                    required
                    disabled={true}
                    value={bookingId}
                  />

                  <DynamicInput
                    name="bookingDateTime"
                    label="Booking Date"
                    type="text"
                    placeholder="Booking Date"
                    required
                    disabled={true}
                    value={formatDate(data?.createdAt)}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: { xs: 2, md: 2 },
                  }}
                >
                  <TextField
                    id="standard-basic"
                    label="Number of Travellers"
                    variant="standard"
                    value={data?.numberOfTravellers}
                    aria-readonly
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    fullWidth
                  />
                  <TextField
                    id="standard-basic"
                    label="Pickup Location"
                    variant="standard"
                    value={data?.pickupLocation}
                    aria-readonly
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    fullWidth
                  />
                  <TextField
                    id="standard-basic"
                    label="Dropoff Location"
                    variant="standard"
                    value={data?.dropLocation}
                    aria-readonly
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    fullWidth
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={12} display={"flex"} gap={2}>
                <TextField
                  id="standard-basic"
                  label="DB_User Name"
                  variant="standard"
                  value={data?.name || "N/A"}
                  aria-readonly
                  disabled
                  fullWidth
                />
                <TextField
                  id="standard-basic"
                  label="DB_User Number"
                  variant="standard"
                  value={data?.phone || "N/A"}
                  aria-readonly
                  disabled
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.02)",
                  }}
                >
                  <Typography
                    variant="h6"
                    color="primary"
                    gutterBottom
                    sx={{
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    Price Breakdown
                  </Typography>

                  {data?.priceTiers?.map((item, idx) => (
                    <Box key={idx}>
                      {/* {idx > 0 && <Divider sx={{ my: 1.5 }} />} */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          py: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          variant={isMobile ? "body2" : "body1"}
                          color="text.primary"
                          sx={{ fontWeight: 500 }}
                        >
                          {item?.name} × {item?.numberOfSeats}
                        </Typography>
                        <Typography
                          variant={isMobile ? "body2" : "body1"}
                          color="text.primary"
                          sx={{ fontWeight: 600 }}
                        >
                          {formatPrice(item?.price,true)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}

                   {isDiscountApplied && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="success"
                        sx={{ fontWeight: 500 }}
                      >
                        Coupon Discount
                      </Typography>
                      <Typography
                        variant="body1"
                        color="success"
                        sx={{ fontWeight: 600 }}
                      >
                        - {formatPrice(data?.priceBreakUp?.couponDiscount as string,true)}
                      </Typography>
                    </Box>
                  )}

                  {showDiscountedPrice && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="text.primary"
                        sx={{ fontWeight: 500 }}
                      >
                        Price After Discount
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.primary"
                        sx={{ fontWeight: 600 }}
                      >
                        {formatPrice(data?.priceBreakUp?.discountedPrice as string,true)}
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 1,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "body2" : "body1"}
                      color="text.primary"
                      sx={{ fontWeight: 500 }}
                    >
                      GST
                    </Typography>
                    <Typography
                      variant={isMobile ? "body2" : "body1"}
                      color="text.primary"
                      sx={{ fontWeight: 600 }}
                    >
                      {formatPrice(data?.priceBreakUp?.gst,true)}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2, borderStyle: "dashed" }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 1,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      color="text.primary"
                    >
                      Total
                    </Typography>
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      color="primary"
                      sx={{ fontWeight: 600 }}
                    >
                      {formatPrice(data?.totalTripCost,true)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Typography
                variant="h6"
                color="primary"
                gutterBottom
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 600,
                    textAlign: "center",
                    width: "100%",
                    mt: 2,
                }}
              >
                Trip Duration :{" "}
                {`${duration?.days} Days / ${duration?.nights} Nights`}
              </Typography>

              <Grid item xs={12} sx={{ mt: { xs: 1, sm: 2 } }}>
                <TravellerDetails 
                  data={data?.travellerDetails} 
                  showContactInfo={shouldShowContactInfo()}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

const TravellerDetails: React.FC<{ 
  data: TravellerDetail[];
  showContactInfo: boolean; 
}> = ({ 
  data,
  showContactInfo 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!data || data.length === 0) {
    return (
      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No traveller details available
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
      }}
    >
      <Box display={"flex"} justifyContent={"space-between"} gap={2}>
        <Typography
          variant={isMobile ? "subtitle1" : "h6"}
          color="primary"
          gutterBottom
          sx={{
            display: "flex",
            alignItems: "center",
            mb: { xs: 2, sm: 3 },
            fontWeight: 600,
          }}
        >
          <PersonIcon sx={{ mr: 1 }} /> Traveller Details
        </Typography>
        {showContactInfo && data[0]?.email && (
          <Typography
            variant="h6"
            color="primary"
            gutterBottom
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 3,
              fontWeight: 500,
            }}
          >
            <Email sx={{ mr: 1 }} /> Email : {data[0].email}
          </Typography>
        )}
      </Box>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          overflow: "auto",
          "&::-webkit-scrollbar": {
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.2)"
                : "rgba(0,0,0,0.2)",
            borderRadius: "4px",
          },
        }}
      >
        <Table
          aria-label="traveller details table"
          size={isMobile ? "small" : "medium"}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  padding: isMobile ? "8px" : "16px",
                }}
              >
                No.
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  padding: isMobile ? "8px" : "16px",
                }}
              >
                Name
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  padding: isMobile ? "8px" : "16px",
                }}
              >
                Gender
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  padding: isMobile ? "8px" : "16px",
                }}
              >
                Age
              </TableCell>
              {showContactInfo && (
                <TableCell
                  sx={{
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    padding: isMobile ? "8px" : "16px",
                  }}
                >
                  Number
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((traveller, index) => (
              <TableRow
                key={traveller.id || index}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <TableCell sx={{ padding: isMobile ? "8px" : "16px" }}>
                  {index + 1}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 500, padding: isMobile ? "8px" : "16px" }}
                >
                  {traveller.name}
                </TableCell>
                <TableCell sx={{ padding: isMobile ? "8px" : "16px" }}>
                  {traveller.gender}
                </TableCell>
                <TableCell sx={{ padding: isMobile ? "8px" : "16px" }}>
                  {traveller.age}
                </TableCell>
                {showContactInfo && (
                  <TableCell sx={{ padding: isMobile ? "8px" : "16px" }}>
                    {traveller?.number}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default BookingDetails;